import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: formData.get('name')?.toString() || '',
      phone: formData.get('phone')?.toString() || '',
      service: formData.get('service')?.toString() || '',
      date: formData.get('date')?.toString() || '',
      message: formData.get('message')?.toString() || '',
      company: formData.get('company')?.toString() || '',
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error('Request failed');
      }

      setSubmitStatus('success');
      form.reset();
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      aria-labelledby="contact-heading"
      className="relative overflow-hidden bg-gradient-to-b from-[#081423] via-[#0B1F3A] to-[#102845] py-24 md:py-28"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[-8%] top-16 h-72 w-72 rounded-full bg-[#38BDF8]/10 blur-3xl" />
        <div className="absolute right-[-8%] bottom-10 h-80 w-80 rounded-full bg-[#60A5FA]/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_24%,rgba(56,189,248,0.08),transparent_28%),radial-gradient(circle_at_82%_70%,rgba(125,211,252,0.06),transparent_26%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#38BDF8]/20 bg-white/5 px-4 py-2 backdrop-blur-xl">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#38BDF8]" />
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-[#D7F0FF]">
              Kontakt · Rückruf · Soforthilfe
            </span>
          </div>

          <h2
            id="contact-heading"
            className="mt-6 text-3xl font-semibold text-white md:text-5xl lg:text-[3.25rem]"
          >
            Direkt Kontakt aufnehmen.
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#D7E8F8] md:text-lg">
            Für akute Fälle bitte direkt anrufen. Für Rückrufwünsche, Rückfragen oder planbare Anliegen kannst du uns dein Anliegen hier schnell und sauber übermitteln.
          </p>
        </div>

        <div className="mt-12 grid gap-8 xl:grid-cols-[0.9fr,1.1fr] xl:items-start">
          <div className="space-y-6">
            <div className="rounded-[2rem] border border-white/10 bg-white/[0.05] p-6 backdrop-blur-2xl shadow-[0_24px_80px_rgba(0,0,0,0.22)] md:p-7">
              <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <Button
                  href="tel:+4915214468574"
                  size="md"
                  className="gap-2 px-6 py-3 rounded-2xl bg-[#38BDF8] !text-[#07111F] border border-[#7DD3FC]/80 shadow-[0_14px_35px_rgba(56,189,248,0.3)] hover:bg-[#7DD3FC] hover:shadow-[0_20px_45px_rgba(56,189,248,0.4)] tracking-[0.14em] uppercase text-[0.72rem]"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="h-4 w-4 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.35 1.78.68 2.62a2 2 0 0 1-.45 2.11L8.03 9.97a16 16 0 0 0 6 6l1.52-1.26a2 2 0 0 1 2.11-.45c.84.33 1.72.56 2.62.68A2 2 0 0 1 22 16.92z" />
                  </svg>
                  Jetzt anrufen
                </Button>

                <Button
                  href="https://wa.me/4915214468574"
                  size="md"
                  className="gap-2 px-6 py-3 rounded-2xl bg-white/8 text-white border border-white/15 backdrop-blur-xl shadow-[0_14px_35px_rgba(0,0,0,0.22)] hover:bg-white/12 tracking-[0.14em] uppercase text-[0.72rem]"
                >
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="h-4 w-4 shrink-0"
                    fill="currentColor"
                  >
                    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.52 0 .18 5.34.18 11.88c0 2.1.54 4.14 1.56 5.94L0 24l6.36-1.68a11.8 11.8 0 0 0 5.7 1.44h.06c6.54 0 11.88-5.34 11.88-11.88 0-3.18-1.26-6.18-3.48-8.4ZM12.12 21.72h-.06a9.78 9.78 0 0 1-4.98-1.38l-.36-.18-3.78 1.02 1.02-3.66-.24-.36a9.8 9.8 0 0 1-1.5-5.28C2.22 6.48 6.66 2.04 12.12 2.04c2.58 0 5.04 1.02 6.84 2.82a9.63 9.63 0 0 1 2.82 6.84c0 5.46-4.44 9.9-9.66 9.9Zm5.4-7.38c-.3-.18-1.8-.9-2.1-1.02-.3-.12-.48-.18-.72.18-.18.3-.72 1.02-.9 1.2-.18.18-.3.24-.6.06-.3-.18-1.2-.42-2.28-1.38-.84-.72-1.44-1.62-1.62-1.92-.18-.3 0-.42.12-.6.12-.12.3-.3.42-.48.12-.18.18-.3.3-.48.12-.18.06-.36-.06-.54-.12-.18-.72-1.74-.96-2.34-.24-.6-.48-.54-.72-.54h-.6c-.18 0-.48.06-.72.3-.24.24-.9.9-.9 2.16 0 1.26.9 2.46 1.08 2.64.18.18 1.8 2.76 4.38 3.84.6.24 1.08.42 1.44.54.6.18 1.14.18 1.56.12.48-.06 1.5-.6 1.68-1.2.24-.6.24-1.08.18-1.2-.06-.12-.24-.18-.54-.36Z" />
                  </svg>
                  WhatsApp schreiben
                </Button>
              </div>

              <div className="mt-6 rounded-[1.6rem] border border-white/10 bg-[#07111F]/50 p-5 md:p-6">
                <p className="text-[0.68rem] uppercase tracking-[0.2em] text-[#7DD3FC]">Ablauf</p>
                <h3 className="mt-3 text-2xl font-semibold text-white md:text-3xl">
                  So läuft deine Anfrage.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#D7E8F8] md:text-base">
                  Schick uns kurz dein Anliegen mit Standort, Fahrzeugtyp und deiner Telefonnummer. Wir prüfen alles und melden uns schnell mit Rückruf, Rückfrage oder direkter Einschätzung.
                </p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-[#38BDF8]/15 bg-[linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.03))] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.18)] backdrop-blur-2xl md:p-7">
              <p className="text-[0.68rem] uppercase tracking-[0.2em] text-[#7DD3FC]">Wichtig</p>
              <div className="mt-5 space-y-4 text-sm md:text-base">
                <div className="flex items-start gap-3">
                  <span className="mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full bg-[#38BDF8]" />
                  <span className="text-[#D7E8F8]">Für akute Notfälle bitte immer direkt anrufen. Das Formular ist nicht für Soforteinsätze gedacht.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full bg-[#38BDF8]" />
                  <span className="text-[#D7E8F8]">Falschparker, Transporte, Rückrufwünsche oder planbare Anliegen kannst du hier sauber anfragen.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full bg-[#38BDF8]" />
                  <span className="text-[#D7E8F8]">Je genauer dein Standort und dein Fahrzeug beschrieben sind, desto schneller können wir reagieren.</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[2.2rem] border border-white/10 bg-white/[0.06] p-6 shadow-[0_30px_100px_rgba(0,0,0,0.24)] backdrop-blur-2xl md:p-7">
            <div className="rounded-[1.8rem] border border-[#38BDF8]/15 bg-[#07111F]/55 p-5 md:p-6">
              <form className="grid gap-6 md:grid-cols-2 text-left" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="company"
                  autoComplete="off"
                  className="hidden"
                  tabIndex={-1}
                />

                <div className="md:col-span-2">
                  <p className="text-sm leading-relaxed text-[#D7E8F8]">
                    Rückruf anfordern oder Anliegen senden. Für akute Einsätze bitte direkt anrufen.
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="contact-name"
                    className="mb-2 block text-sm font-medium text-white"
                  >
                    Name*
                  </label>
                  <input
                    type="text"
                    required
                    id="contact-name"
                    className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-[#0F172A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                    name="name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-phone"
                    className="mb-2 block text-sm font-medium text-white"
                  >
                    Telefonnummer*
                  </label>
                  <input
                    type="tel"
                    required
                    id="contact-phone"
                    className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-[#0F172A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                    name="phone"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-service"
                    className="mb-2 block text-sm font-medium text-white"
                  >
                    Anliegen*
                  </label>
                  <select
                    required
                    id="contact-service"
                    className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-[#0F172A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                    name="service"
                  >
                    <option value="">Bitte auswählen</option>
                    <option value="pannenhilfe">Pannenhilfe / Fahrzeug startet nicht</option>
                    <option value="unfall">Unfall / Bergung</option>
                    <option value="falschparker">Falschparker auf Privatgrund</option>
                    <option value="hof-tiefgarage">Fahrzeug in Hof / Tiefgarage</option>
                    <option value="motorrad-roller">Motorrad / Roller</option>
                    <option value="transport">Fahrzeugtransport</option>
                    <option value="sonstiges">Sonstiges Anliegen</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="contact-date"
                    className="mb-2 block text-sm font-medium text-white"
                  >
                    Datum &amp; Uhrzeit (optional)
                  </label>
                  <input
                    type="datetime-local"
                    id="contact-date"
                    className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-[#0F172A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                    name="date"
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="contact-message"
                    className="mb-2 block text-sm font-medium text-white"
                  >
                    Nachricht
                  </label>
                  <textarea
                    rows={5}
                    id="contact-message"
                    className="w-full rounded-2xl border border-white/10 bg-white/95 px-4 py-3 text-[#0F172A] focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#38BDF8]"
                    placeholder="z. B. Standort, Fahrzeugtyp, Kennzeichen, was passiert ist, erreichbare Telefonnummer"
                    name="message"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="contact-privacy" className="flex cursor-pointer items-start gap-2 text-xs text-[#D7E8F8]">
                    <input
                      type="checkbox"
                      required
                      id="contact-privacy"
                      name="privacy"
                      className="mt-1"
                    />
                    <span>
                      Ich habe die <a href="/datenschutz" className="underline underline-offset-2 hover:text-white">Datenschutzerklärung</a> gelesen und stimme der Verarbeitung meiner Angaben zu.
                    </span>
                  </label>
                </div>

                <div className="md:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    className="rounded-2xl border border-[#7DD3FC]/70 bg-[#38BDF8] px-8 py-3 text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-[#07111F] shadow-[0_18px_40px_rgba(56,189,248,0.3)] transition-all duration-300 hover:bg-[#7DD3FC] hover:shadow-[0_24px_55px_rgba(56,189,248,0.42)] sm:text-sm md:text-base"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird gesendet…' : 'Nachricht senden'}
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <div className="md:col-span-2 text-left text-sm text-[#86EFAC]">
                    Deine Anfrage wurde erfolgreich gesendet.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="md:col-span-2 text-left text-sm text-[#FCA5A5]">
                    Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
